import { createFileRoute, Link } from '@tanstack/react-router';
import { ArrowLeft } from 'lucide-react';
import { Header, PageContainer } from '@/components/layout';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  StatusBadge,
  PriorityBadge,
  Badge,
  Avatar,
} from '@/components/ui';
import { useTask } from '@/hooks/useTasks';
import { formatDate } from '@aelita/shared';

export const Route = createFileRoute('/tasks/$taskId')({
  component: TaskDetailPage,
});

function TaskDetailPage() {
  const { taskId } = Route.useParams();
  const { data: task, isLoading } = useTask(taskId);

  if (isLoading) {
    return (
      <>
        <Header title="Loading..." />
        <PageContainer>
          <p className="text-gray-500 dark:text-gray-400">Loading task details...</p>
        </PageContainer>
      </>
    );
  }

  if (!task) {
    return (
      <>
        <Header title="Task Not Found" />
        <PageContainer>
          <p className="text-gray-500 dark:text-gray-400">The task you&apos;re looking for doesn&apos;t exist.</p>
        </PageContainer>
      </>
    );
  }

  const taskAny = task as any;

  return (
    <>
      <Header
        title={taskAny.title}
        subtitle={taskAny.project?.name}
        actions={
          <div className="flex items-center gap-2">
            <Link to="/tasks">
              <Button variant="ghost" leftIcon={<ArrowLeft className="h-4 w-4" />}>
                Back
              </Button>
            </Link>
            <Button>Edit Task</Button>
          </div>
        }
      />
      <PageContainer>
        <div className="grid grid-cols-3 gap-6">
          <div className="col-span-2 space-y-6">
            {/* Task Info */}
            <Card>
              <CardHeader className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Badge variant="gray">{taskAny.type}</Badge>
                  <StatusBadge status={taskAny.status} />
                  <PriorityBadge priority={taskAny.priority} />
                </div>
              </CardHeader>
              <CardBody>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">{taskAny.title}</h2>
                {taskAny.description ? (
                  <div className="prose prose-sm max-w-none">
                    <p className="text-gray-600 dark:text-gray-300 whitespace-pre-wrap">{taskAny.description}</p>
                  </div>
                ) : (
                  <p className="text-gray-400 italic">No description provided</p>
                )}
              </CardBody>
            </Card>

            {/* Subtasks */}
            {taskAny.subtasks?.length > 0 && (
              <Card>
                <CardHeader>
                  <h2 className="font-semibold">Subtasks</h2>
                </CardHeader>
                <CardBody>
                  <div className="space-y-2">
                    {taskAny.subtasks.map((subtask: any) => (
                      <div
                        key={subtask.id}
                        className="flex items-center gap-3 p-2 bg-gray-50 dark:bg-gray-800 rounded"
                      >
                        <input
                          type="checkbox"
                          checked={subtask.status === 'done'}
                          readOnly
                          className="h-4 w-4 text-primary-600 dark:text-primary-400"
                        />
                        <span
                          className={subtask.status === 'done' ? 'line-through text-gray-400' : ''}
                        >
                          {subtask.title}
                        </span>
                      </div>
                    ))}
                  </div>
                </CardBody>
              </Card>
            )}

            {/* Comments */}
            <Card>
              <CardHeader className="flex items-center justify-between">
                <h2 className="font-semibold">Comments</h2>
              </CardHeader>
              <CardBody>
                {taskAny.comments?.length > 0 ? (
                  <div className="space-y-4">
                    {taskAny.comments.map((comment: any) => (
                      <div key={comment.id} className="flex gap-3">
                        <Avatar
                          src={comment.employee?.avatarUrl}
                          name={`${comment.employee?.firstName} ${comment.employee?.lastName}`}
                          size="sm"
                        />
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-gray-900 dark:text-gray-100">
                              {comment.employee?.firstName} {comment.employee?.lastName}
                            </span>
                            <span className="text-xs text-gray-400">
                              {formatDate(new Date(comment.createdAt))}
                            </span>
                          </div>
                          <p className="text-gray-600 dark:text-gray-300 mt-1">{comment.content}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 dark:text-gray-400">No comments yet</p>
                )}

                {/* Add comment form */}
                <div className="mt-4 pt-4 border-t">
                  <textarea className="input min-h-[60px]" placeholder="Add a comment..." />
                  <div className="mt-2 flex justify-end">
                    <Button size="sm">Add Comment</Button>
                  </div>
                </div>
              </CardBody>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Details */}
            <Card>
              <CardHeader>
                <h2 className="font-semibold">Details</h2>
              </CardHeader>
              <CardBody className="space-y-4">
                <div>
                  <dt className="text-sm text-gray-500 dark:text-gray-400">Project</dt>
                  <dd className="mt-1">
                    <Link
                      to="/project/$projectId"
                      params={{ projectId: taskAny.project?.id }}
                      className="font-medium text-primary-600 dark:text-primary-400 hover:underline"
                    >
                      {taskAny.project?.name}
                    </Link>
                  </dd>
                </div>
                {taskAny.milestone && (
                  <div>
                    <dt className="text-sm text-gray-500 dark:text-gray-400">Milestone</dt>
                    <dd className="mt-1 font-medium">{taskAny.milestone.name}</dd>
                  </div>
                )}
                <div>
                  <dt className="text-sm text-gray-500 dark:text-gray-400">Due Date</dt>
                  <dd className="mt-1 font-medium">
                    {taskAny.dueDate ? formatDate(new Date(taskAny.dueDate)) : 'Not set'}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm text-gray-500 dark:text-gray-400">Estimated</dt>
                  <dd className="mt-1 font-medium">
                    {taskAny.estimatedHours ? `${taskAny.estimatedHours}h` : 'Not estimated'}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm text-gray-500 dark:text-gray-400">Time Logged</dt>
                  <dd className="mt-1 font-medium">
                    {taskAny.actualHours ? `${taskAny.actualHours.toFixed(1)}h` : '0h'}
                  </dd>
                </div>
              </CardBody>
            </Card>

            {/* Assignees */}
            <Card>
              <CardHeader className="flex items-center justify-between">
                <h2 className="font-semibold">Assignees</h2>
                <Button size="sm" variant="ghost">
                  Add
                </Button>
              </CardHeader>
              <CardBody>
                {taskAny.assignments?.length > 0 ? (
                  <div className="space-y-3">
                    {taskAny.assignments.map((assignment: any) => (
                      <div key={assignment.id} className="flex items-center gap-3">
                        <Avatar
                          src={assignment.employee?.avatarUrl}
                          name={`${assignment.employee?.firstName} ${assignment.employee?.lastName}`}
                        />
                        <div>
                          <p className="font-medium text-gray-900 dark:text-gray-100">
                            {assignment.employee?.firstName} {assignment.employee?.lastName}
                          </p>
                          <p className="text-sm text-gray-500 dark:text-gray-400">{assignment.employee?.email}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 dark:text-gray-400 text-sm">No one assigned</p>
                )}
              </CardBody>
            </Card>

            {/* Labels */}
            {taskAny.labels?.length > 0 && (
              <Card>
                <CardHeader>
                  <h2 className="font-semibold">Labels</h2>
                </CardHeader>
                <CardBody>
                  <div className="flex flex-wrap gap-2">
                    {taskAny.labels.map((label: string) => (
                      <Badge key={label} variant="gray">
                        {label}
                      </Badge>
                    ))}
                  </div>
                </CardBody>
              </Card>
            )}
          </div>
        </div>
      </PageContainer>
    </>
  );
}
